import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { ProfileResponse } from '../models/auth.model';

/**
 * The onboarding gate.
 *
 * Replaces authGuard, which allowed a user in only if `is_phone_verified` was
 * true and bounced everyone else to /auth/get-started. That gave exactly two
 * outcomes — fully in, or thrown out of the whole app — so a Google user who
 * had just signed up and had no phone number yet was treated like a stranger:
 * kicked to the front door, from where signing in again produced the same
 * bounce. A loop, with no screen that could collect what was missing.
 *
 * This routes on what the account is actually missing, which the API decides
 * and reports in `onboarding.next_step`. The browser never works it out for
 * itself, so the two cannot disagree.
 *
 *   not signed in   → the login card
 *   complete        → the completion screen, which collects name/phone/email
 *   otp             → the OTP screen, to prove the phone number
 *   dashboard       → through to the page they asked for
 *
 * A guard in the browser is a convenience, not a control: anyone can edit a
 * URL. The same rule is enforced server-side by the `require_onboarded`
 * dependency, which is what actually protects payment and (once they exist)
 * order routes.
 */

/** Where each gate outcome sends the user. One place, so B can confirm them. */
export const AUTH_ROUTE = '/auth/get-started';
export const GATE_TARGETS: Record<string, { route: string; step?: string }> = {
  signedOut: { route: AUTH_ROUTE },
  complete: { route: AUTH_ROUTE, step: 'complete' },
  otp: { route: AUTH_ROUTE, step: 'otp' },
};

function gateFor(router: Router, user: ProfileResponse | null): boolean | UrlTree {
  if (!user) {
    return router.createUrlTree([GATE_TARGETS['signedOut'].route]);
  }

  // Defensive: an API that predates the onboarding block should not lock
  // anyone out of the app. Absent means "nothing known to be missing".
  const nextStep = user.onboarding?.next_step ?? 'dashboard';

  if (nextStep === 'dashboard') {
    return true;
  }

  const target = GATE_TARGETS[nextStep];
  if (!target) {
    return true;
  }

  return router.createUrlTree(
    [target.route],
    target.step ? { queryParams: { step: target.step } } : {},
  );
}

export const onboardingGuard: CanActivateFn = (): Observable<boolean | UrlTree> | boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Already in memory: decide without a round trip. The signal is kept current
  // by every call that returns a profile, so this is not a stale read.
  const cached = authService.currentUser();
  if (cached) {
    return gateFor(router, cached);
  }

  return authService.checkAuthStatus().pipe(
    map((user) => gateFor(router, user)),
    // A network failure is not proof of being signed out, but it is not proof
    // of being signed in either — send them to the card rather than into a
    // dashboard that cannot load its data.
    catchError(() => of(router.createUrlTree([GATE_TARGETS['signedOut'].route]))),
  );
};
